<template>
  <div class="w-full max-w-[100vw] overflow-x-hidden pt-10 pb-12 relative">
    <!-- Swiper -->
    <Swiper
        :modules="[EffectCoverflow, Pagination]"
        effect="coverflow"
        :grabCursor="true"
        :centeredSlides="true"
        :loop="true"
        :watchSlidesProgress="true"
        slidesPerView="auto"
        :speed="800"
        :coverflowEffect="{
          rotate: 0,
          stretch: 80,
          depth: 350,
          modifier: 1,
          slideShadows: false
        }"
        :pagination="{ clickable: true, dynamicBullets: true }"
        :initialSlide="0"
        @swiper="onSwiperInit"
        @progress="onSwiperProgress"
        class="mySwiper !pb-14"
    >
      <SwiperSlide
          v-for="(item, index) in normalizedItems"
          :key="index"
          class="swiper-slide-custom group"
      >
        <div class="slide-content rounded-3xl overflow-hidden relative w-full h-full bg-black border-[1px] border-white/10">
          <component
              :is="item.type === 'youtube' ? 'iframe' : 'img'"
              v-bind="getMediaProps(item)"
              class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <!-- Shine Effect -->
          <div class="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
        </div>
      </SwiperSlide>
    </Swiper>

    <!-- Custom Navigation Arrows (Gamified) -->
    <button
        class="absolute left-1 top-1/2 z-30 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-md border border-white/20 text-white transition-all hover:bg-white/20 hover:scale-110 active:scale-95"
        @click="swiperInstance?.slidePrev()"
        aria-label="Anterior"
    >
      <svg class="w-6 h-6 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7" />
      </svg>
    </button>
    <button
        class="absolute right-1 top-1/2 z-30 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-md border border-white/20 text-white transition-all hover:bg-white/20 hover:scale-110 active:scale-95"
        @click="swiperInstance?.slideNext()"
        aria-label="Próximo"
    >
      <svg class="w-6 h-6 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { EffectCoverflow, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/effect-coverflow'
import 'swiper/css/pagination'

const props = defineProps({
  images: { type: Array, required: true },
  pageColor: { type: String, default: '#00a0f1' }, // Default "Mito Blue"
})

const swiperInstance = ref(null)

function onSwiperInit(swiper) {
  swiperInstance.value = swiper
  updateSlideVisibility(swiper)
}

function onSwiperProgress(swiper) {
  updateSlideVisibility(swiper)
}

function updateSlideVisibility(swiper) {
  swiper.slides.forEach((slide) => {
    const progress = slide.progress
    const content = slide.querySelector('.slide-content')
    if (!content) return

    const absProgress = Math.abs(progress)

    if (absProgress < 0.5) {
      // Ativo
      content.style.opacity = '1'
      content.style.transform = 'scale(1)'
      content.style.filter = 'none'
    } else if (absProgress <= 2.5) {
      // 1 ou 2 posições de distância (Fundo)
      content.style.opacity = '0.4'
      content.style.transform = 'scale(0.85)'
      content.style.filter = 'blur(1px) grayscale(50%)'
    } else {
      // Muito longe (Ocultar completamente para evitar scroll horizontal)
      content.style.opacity = '0'
      content.style.pointerEvents = 'none'
      // Opcional: display none pode interferir no calculo inicial, mas no progress state ok
      // content.style.visibility = 'hidden' 
    }
  })
}

const normalizedItems = computed(() =>
    (Array.isArray(props.images) ? props.images : [])
        .filter(Boolean)
        .map((item) => (typeof item === 'string' ? { type: 'image', src: item } : item))
)

function getMediaProps(item) {
  if (!item) return {}
  if (item.type === 'youtube') {
    const id = item.src.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/)
    return {
      src: id ? `https://www.youtube.com/embed/${id[1]}` : '',
      frameborder: '0',
      allowfullscreen: true,
      title: 'YouTube video player'
    }
  }
  return { src: item.src, alt: 'Imagem do jogo' }
}
</script>

<style scoped>
.mySwiper {
  width: 100%;
  perspective: 1200px;
  overflow: visible !important; /* Permitir que o brilho e sombras vazem */
}

.swiper-slide-custom {
  /* Tamanho base do slide - Vertical Ratio ~9:16 */
  width: 280px;
  height: 500px;
  transition: all 0.5s cubic-bezier(0.25, 1, 0.5, 1);
  /* Centering */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.slide-content {
  transition: all 0.5s ease;
  transform-origin: center center;
  /* Estado inativo */
  opacity: 0.5;
  filter: blur(2px) grayscale(60%);
  transform: scale(0.9);
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
}

/* --- ESTADO ATIVO (GAMIFICADO) --- */
.swiper-slide-active {
  z-index: 50;
}

.swiper-slide-active .slide-content {
  opacity: 1;
  filter: none;
  border-color: rgba(255, 255, 255, 0.5);
  
  /* Brilho Neon Dinâmico */
  box-shadow: 
      0 10px 40px -10px rgba(0,0,0,0.8),
      0 0 20px -5px v-bind('pageColor');
  
  /* Animação de flutuação */
  animation: float-pulse 4s ease-in-out infinite;
}

/* Keyframes para o efeito "Float/Breathe" */
@keyframes float-pulse {
  0%, 100% {
    transform: scale(1) translateY(0);
  }
  50% {
    transform: scale(1.02) translateY(-8px);
    box-shadow: 
        0 20px 50px -10px rgba(0,0,0,0.9),
        0 0 35px -5px v-bind('pageColor');
  }
}

/* Responsividade */
@media (min-width: 640px) {
  .swiper-slide-custom {
    width: 320px;
    height: 560px;
  }
}

@media (min-width: 1024px) {
  .swiper-slide-custom {
    width: 310px;
    height: 550px;
  }
}

/* Pagination Customization */
:deep(.swiper-pagination-bullet) {
  background: white;
  opacity: 0.3;
  width: 8px;
  height: 8px;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

:deep(.swiper-pagination-bullet-active) {
  opacity: 1;
  width: 32px;
  border-radius: 10px;
  background: v-bind('pageColor');
  box-shadow: 0 0 10px v-bind('pageColor');
}
</style>
